import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Edit3, ExternalLink, Package, X, Save } from "lucide-react";

const INPUT_CLS = "w-full bg-[var(--input-bg)] border border-[var(--border)] focus:border-[var(--border-strong)] focus:outline-none px-3 py-2 text-[13px] text-[var(--text-primary)]";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">{label}</label>
      {children}
    </div>
  );
}

function ProductForm({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    tagline: initial?.tagline || "",
    description: initial?.description || "",
    landing_url: initial?.landing_url || "",
    key_features: (initial?.key_features || []).join("\n"),
    target_keywords: (initial?.target_keywords || []).join(", "),
    pitch_angles: (initial?.pitch_angles || []).join("\n"),
    tone: initial?.tone || "",
    do_not_say: (initial?.do_not_say || []).join(", "),
  });

  const save = useMutation({
    mutationFn: (body) => initial ? api.patch(`/products/${initial.id}`, body) : api.post("/products", body),
    onSuccess: () => { toast.success(initial ? "Product updated" : "Product created"); onSaved?.(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const submit = () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    save.mutate({
      name: form.name.trim(),
      tagline: form.tagline.trim(),
      description: form.description.trim(),
      landing_url: form.landing_url.trim(),
      key_features: form.key_features.split("\n").map(x => x.trim()).filter(Boolean),
      target_keywords: form.target_keywords.split(",").map(x => x.trim()).filter(Boolean),
      pitch_angles: form.pitch_angles.split("\n").map(x => x.trim()).filter(Boolean),
      tone: form.tone.trim(),
      do_not_say: form.do_not_say.split(",").map(x => x.trim()).filter(Boolean),
    });
  };

  const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  return (
    <div className="border border-[var(--border)] bg-[var(--surface)] p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-black text-2xl tracking-tight">{initial ? "Edit brand." : "New brand."}</h3>
        {onCancel && <button onClick={onCancel} className="p-1.5 border border-[var(--border)] hover:border-[var(--border-strong)]"><X className="w-4 h-4"/></button>}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Name *"><input className={INPUT_CLS} data-testid="product-name" value={form.name} onChange={set("name")}/></Field>
        <Field label="Tagline"><input className={INPUT_CLS} data-testid="product-tagline" value={form.tagline} onChange={set("tagline")}/></Field>
      </div>
      <Field label="Description"><textarea rows={3} className={INPUT_CLS} data-testid="product-desc" value={form.description} onChange={set("description")}/></Field>
      <Field label="Landing URL"><input className={`${INPUT_CLS} font-mono`} data-testid="product-url" placeholder="https://…" value={form.landing_url} onChange={set("landing_url")}/></Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Key features (one per line)"><textarea rows={4} className={INPUT_CLS} data-testid="product-features" value={form.key_features} onChange={set("key_features")}/></Field>
        <Field label="Pitch angles (one per line)"><textarea rows={4} className={INPUT_CLS} data-testid="product-pitches" placeholder="e.g. 'saves 3 hrs/week'" value={form.pitch_angles} onChange={set("pitch_angles")}/></Field>
      </div>
      <Field label="Target keywords (comma-separated)"><input className={INPUT_CLS} data-testid="product-keywords" value={form.target_keywords} onChange={set("target_keywords")}/></Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Tone (e.g. friendly · technical · witty)"><input className={INPUT_CLS} data-testid="product-tone" value={form.tone} onChange={set("tone")}/></Field>
        <Field label="Do NOT say (comma-separated)"><input className={INPUT_CLS} data-testid="product-dontsay" placeholder="'best in class', 'game-changer'" value={form.do_not_say} onChange={set("do_not_say")}/></Field>
      </div>
      <button
        onClick={submit}
        disabled={save.isPending}
        data-testid="save-product-btn"
        className="w-full bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-medium py-2.5 flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {save.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
        {initial ? "SAVE CHANGES" : "CREATE BRAND"}
      </button>
    </div>
  );
}

export default function Products() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const q = useQuery({
    queryKey: ["products"],
    queryFn: () => api.get("/products").then(r => r.data),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/products/${id}`),
    onSuccess: () => { toast.success("Removed"); qc.invalidateQueries({queryKey:["products"]}); },
  });

  const refresh = () => {
    qc.invalidateQueries({queryKey:["products"]});
    setCreating(false); setEditing(null);
  };

  return (
    <Layout title="Brands & Products." subtitle="What you're promoting"
      right={!creating && !editing && (
        <button onClick={() => setCreating(true)} data-testid="new-product-btn"
          className="bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-medium px-3 py-2 flex items-center gap-2 text-[13px]">
          <Plus className="w-4 h-4"/> New brand
        </button>
      )}
    >
      {creating && <div className="mb-6"><ProductForm onCancel={() => setCreating(false)} onSaved={refresh}/></div>}
      {editing && <div className="mb-6"><ProductForm initial={editing} onCancel={() => setEditing(null)} onSaved={refresh}/></div>}

      <div className="grid md:grid-cols-2 gap-4">
        {q.isLoading && <div className="col-span-2 text-zinc-500 text-sm p-6">Loading…</div>}
        {q.data?.length === 0 && !creating && (
          <div className="col-span-2 border border-dashed border-white/15 p-12 text-center bg-dots">
            <Package className="w-8 h-8 text-zinc-700 mx-auto mb-4"/>
            <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-2">// no brands yet</div>
            <div className="text-zinc-400 text-sm">Add the brand or product you want to promote. Jarvis will weave it into topics organically.</div>
          </div>
        )}
        {q.data?.map((p) => (
          <div key={p.id} data-testid={`product-card-${p.id}`} className="border border-white/10 p-5 hover:border-white/25">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-display font-black text-xl tracking-tight text-white">{p.name}</div>
                {p.tagline && <div className="text-[12px] text-zinc-400 mt-1">{p.tagline}</div>}
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(p)} data-testid={`edit-product-${p.id}`} className="p-1.5 border border-white/10 hover:border-white/30 text-zinc-400 hover:text-white">
                  <Edit3 className="w-3.5 h-3.5"/>
                </button>
                <button onClick={() => { if (window.confirm(`Remove ${p.name}?`)) del.mutate(p.id); }}
                        data-testid={`delete-product-${p.id}`}
                        className="p-1.5 border border-white/10 hover:border-[#FF3B30] text-zinc-400 hover:text-[#FF3B30]">
                  <Trash2 className="w-3.5 h-3.5"/>
                </button>
              </div>
            </div>
            {p.description && <div className="text-[13px] text-zinc-300 line-clamp-2 mb-3">{p.description}</div>}
            {p.landing_url && (
              <a href={p.landing_url} target="_blank" rel="noreferrer"
                 className="font-mono text-[11px] text-[#FF3B30] hover:underline flex items-center gap-1 mb-3 truncate">
                {p.landing_url} <ExternalLink className="w-3 h-3 shrink-0"/>
              </a>
            )}
            {(p.key_features || []).length > 0 && (
              <div className="mb-2">
                <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-1">features</div>
                <div className="flex flex-wrap gap-1">
                  {p.key_features.slice(0,4).map((f,i) => <span key={`${p.id}-feat-${f}-${i}`} className="text-[10px] border border-white/10 px-1.5 py-0.5 text-zinc-300">{f}</span>)}
                </div>
              </div>
            )}
            {(p.target_keywords || []).length > 0 && (
              <div className="mt-2 pt-2 border-t border-white/5 font-mono text-[10px] text-zinc-500">
                keywords: {p.target_keywords.join(" · ")}
              </div>
            )}
          </div>
        ))}
      </div>
    </Layout>
  );
}
